import { useEffect, useRef, useState } from 'react'
import Modal from 'react-modal'
import {
  DEFAULT_MAP_ACTIONS,
  MAP_ACTION_DESCRIPTIONS,
  MAP_ACTION_ICON_CATALOG,
  MapActionConfigItem,
  MapActionState,
  MAX_MAP_ACTION_BADGE_TEXT_LENGTH,
  MAX_MAP_ACTION_DISABLED_MESSAGE_LENGTH,
  MAX_MAP_ACTION_ICON_BYTES,
  MAX_MAP_ACTION_LABEL_LENGTH,
  validateMapActionIconSvg,
} from '@ivy-monorepo/shared'
import { adminService } from '../../../services/admin.service'
import { BaseModalProps } from '../../../types/modal'
import MapActionIcon, { builtInMapActionIconUrl, catalogMapActionIconUrl } from './MapActionIcon'
import MapActionCenterPill, { MapActionMarkerBadge } from './MapActionCenterPill'

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/

const COLOR_PRESETS = Array.from(new Set(DEFAULT_MAP_ACTIONS.map(item => item.color.toUpperCase())))

export const MAP_ACTION_STATE_OPTIONS: { value: MapActionState; label: string; help: string }[] = [
  { value: MapActionState.Active, label: 'Active', help: 'Shown in the sidebar and can be selected.' },
  {
    value: MapActionState.Disabled,
    label: 'Disabled',
    help: 'Shown dimmed and cannot be selected. Tapping it shows the message below (if any).',
  },
  { value: MapActionState.Hidden, label: 'Hidden', help: 'Not shown in the sidebar at all.' },
]

interface MapActionEditModalProps extends BaseModalProps {
  item: MapActionConfigItem | null
  onApply: (item: MapActionConfigItem) => void
}

const MapActionEditModal = ({ isOpen, onClose, item, onApply }: MapActionEditModalProps) => {
  const [draft, setDraft] = useState<MapActionConfigItem | null>(item)
  const [uploading, setUploading] = useState(false)
  const [iconError, setIconError] = useState<string | null>(null)
  const [iconQuery, setIconQuery] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!isOpen) return
    setDraft(item)
    setIconError(null)
    setIconQuery('')
  }, [isOpen, item])

  if (!draft) return null

  const patch = (changes: Partial<MapActionConfigItem>) => setDraft(prev => (prev ? { ...prev, ...changes } : prev))
  const defaults = DEFAULT_MAP_ACTIONS.find(d => d.key === draft.key)!

  const labelValid = draft.label.trim().length > 0 && draft.label.trim().length <= MAX_MAP_ACTION_LABEL_LENGTH
  const badgeTextValid =
    draft.badgeText.trim().length > 0 && draft.badgeText.trim().length <= MAX_MAP_ACTION_BADGE_TEXT_LENGTH
  const colorValid = HEX_COLOR.test(draft.color)
  const valid = labelValid && badgeTextValid && colorValid && !uploading

  const handleIconFile = async (file: File | undefined) => {
    if (!file) return
    setIconError(null)
    if (file.size > MAX_MAP_ACTION_ICON_BYTES) {
      setIconError(`The icon must be at most ${MAX_MAP_ACTION_ICON_BYTES / 1024} KB.`)
      return
    }
    const svgError = validateMapActionIconSvg(await file.text())
    if (svgError) {
      setIconError(svgError.message)
      return
    }
    setUploading(true)
    try {
      patch({ iconUrl: await adminService.uploadMapActionIcon(file), iconName: null })
    } catch (error: any) {
      setIconError(error?.response?.data?.message ?? 'Upload failed.')
    } finally {
      setUploading(false)
    }
  }

  const apply = () => {
    if (!valid) return
    onApply({
      ...draft,
      label: draft.label.trim(),
      badgeText: draft.badgeText.trim(),
      color: draft.color.toUpperCase(),
      disabledMessage: draft.disabledMessage.trim(),
    })
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="modal-popup map-action-modal"
      overlayClassName="modal-overlay"
      closeTimeoutMS={300}
    >
      <div className="modal-content-wrapper">
        <div className="map-action-modal__header">
          <span className="map-action-tile">
            <MapActionIcon item={draft} />
          </span>
          <div>
            <h2>{draft.label || defaults.label}</h2>
            <code>{draft.key}</code>
          </div>
        </div>
        <p className="map-action-modal__description">{MAP_ACTION_DESCRIPTIONS[draft.key]}</p>

        <div className="map-action-modal__body">
          <section className="map-action-modal__section">
            <div className="map-action-modal__section-title map-action-modal__icon-title">
              Icon
              <input
                type="search"
                className="map-filter-input map-action-icon-search"
                placeholder="Search by name…"
                value={iconQuery}
                onChange={e => setIconQuery(e.target.value)}
              />
            </div>
            <div className="map-action-icon-grid" role="radiogroup" aria-label="Icon">
              {(() => {
                const query = iconQuery.trim().toLowerCase()
                const tiles = [
                  { name: null as string | null, label: 'Default', src: builtInMapActionIconUrl(draft.key) },
                  ...MAP_ACTION_ICON_CATALOG.map(icon => ({
                    name: icon.name as string | null,
                    label: icon.label,
                    src: catalogMapActionIconUrl(icon.name),
                  })),
                ].filter(
                  tile =>
                    !query
                    || tile.label.toLowerCase().includes(query)
                    || (tile.name ?? 'default').includes(query),
                )
                if (tiles.length === 0) {
                  return <div className="feature-flag-subtitle">No icon matches "{iconQuery}".</div>
                }
                return tiles.map(tile => {
                  const selected = !draft.iconUrl && draft.iconName === tile.name
                  return (
                    <button
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      key={tile.name ?? 'default'}
                      title={tile.name ?? 'Built-in icon (also the fallback)'}
                      className={`map-action-icon-option${selected ? ' is-selected' : ''}`}
                      onClick={() => patch({ iconName: tile.name, iconUrl: null })}
                    >
                      <span className="map-action-tile">
                        <img className="map-action-icon map-action-icon--muted" src={tile.src} width={24} height={24} alt="" />
                      </span>
                      <span className="map-action-icon-option__label">{tile.label}</span>
                    </button>
                  )
                })
              })()}
            </div>
            <div className="map-action-modal__icon-row">
              {draft.iconUrl ? (
                <span className="map-action-icon-option is-selected map-action-icon-option--static">
                  <span className="map-action-tile">
                    <MapActionIcon item={draft} />
                  </span>
                  <span className="map-action-icon-option__label">Custom</span>
                </span>
              ) : null}
              <div className="map-action-modal__icon-actions">
                <div className="map-action-modal__buttons">
                  <button
                    type="button"
                    className="styled-button sm cancel"
                    disabled={uploading}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {uploading ? 'Uploading…' : draft.iconUrl ? 'Replace custom SVG' : 'Upload custom SVG'}
                  </button>
                </div>
                <div className="feature-flag-subtitle">
                  Icons from the list ship with the app (Heroicons outline). <strong>Default</strong> is the original
                  icon and the fallback when an app version doesn&apos;t know the picked one. A custom SVG
                  (single-color 24×24, up to {MAX_MAP_ACTION_ICON_BYTES / 1024} KB) overrides both. The app tints
                  every icon gray (white when selected).
                </div>
                {iconError ? <div className="map-action-modal__error">{iconError}</div> : null}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".svg,image/svg+xml"
                hidden
                onChange={e => {
                  handleIconFile(e.target.files?.[0])
                  e.target.value = ''
                }}
              />
            </div>
          </section>

          <section className="map-action-modal__section map-action-modal__grid">
            <label className="map-action-modal__field">
              <span className="ticker-setting-label">Name</span>
              <input
                type="text"
                className="map-filter-input"
                maxLength={MAX_MAP_ACTION_LABEL_LENGTH}
                value={draft.label}
                placeholder={defaults.label}
                onChange={e => patch({ label: e.target.value })}
              />
              <span className="feature-flag-subtitle">Sidebar tooltip and screen-reader name.</span>
              {!labelValid ? <span className="map-action-modal__error">Required.</span> : null}
            </label>
            <label className="map-action-modal__field">
              <span className="ticker-setting-label">Pill text</span>
              <input
                type="text"
                className="map-filter-input"
                maxLength={MAX_MAP_ACTION_BADGE_TEXT_LENGTH}
                value={draft.badgeText}
                placeholder={defaults.badgeText}
                onChange={e => patch({ badgeText: e.target.value })}
              />
              <span className="feature-flag-subtitle">Shown at the bottom of the map while selected.</span>
              {!badgeTextValid ? <span className="map-action-modal__error">Required.</span> : null}
            </label>
          </section>

          <section className="map-action-modal__section">
            <div className="map-action-modal__section-title">Color</div>
            <div className="map-action-modal__color-row">
              <input
                type="color"
                className="map-filter-color"
                value={colorValid ? draft.color : '#000000'}
                onChange={e => patch({ color: e.target.value.toUpperCase() })}
              />
              <input
                type="text"
                className="map-filter-input map-filter-hex"
                value={draft.color}
                maxLength={7}
                onChange={e => patch({ color: e.target.value.trim() })}
              />
              <div className="map-action-modal__swatches">
                {COLOR_PRESETS.map(color => (
                  <button
                    type="button"
                    key={color}
                    title={color}
                    className={`map-action-swatch${draft.color.toUpperCase() === color ? ' is-selected' : ''}`}
                    style={{ background: color }}
                    onClick={() => patch({ color })}
                  />
                ))}
              </div>
            </div>
            {!colorValid ? <div className="map-action-modal__error">Use a #RRGGBB hex value.</div> : null}
            <div className="map-action-modal__preview">
              <span className="feature-flag-subtitle">Marker badge</span>
              <MapActionMarkerBadge item={draft} />
              <span className="feature-flag-subtitle">Pill</span>
              <MapActionCenterPill item={draft} />
            </div>
          </section>

          <section className="map-action-modal__section">
            <div className="map-action-modal__section-title">Availability</div>
            <div className="map-action-state-segmented" role="radiogroup">
              {MAP_ACTION_STATE_OPTIONS.map(option => (
                <button
                  type="button"
                  role="radio"
                  aria-checked={draft.state === option.value}
                  key={option.value}
                  className={`map-action-state-segmented__option map-action-state--${option.value}${
                    draft.state === option.value ? ' is-selected' : ''
                  }`}
                  onClick={() => patch({ state: option.value })}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div className="feature-flag-subtitle">
              {MAP_ACTION_STATE_OPTIONS.find(option => option.value === draft.state)?.help}
            </div>
            {draft.state === MapActionState.Disabled ? (
              <label className="map-action-modal__field map-action-modal__field--spaced">
                <span className="ticker-setting-label">Message on tap (optional)</span>
                <input
                  type="text"
                  className="map-filter-input"
                  maxLength={MAX_MAP_ACTION_DISABLED_MESSAGE_LENGTH}
                  value={draft.disabledMessage}
                  placeholder="e.g. Coming soon"
                  onChange={e => patch({ disabledMessage: e.target.value })}
                />
              </label>
            ) : null}
          </section>
        </div>

        <div className="modal-footer-content">
          <button
            type="button"
            className="styled-button cancel map-action-modal__reset"
            onClick={() => setDraft({ ...defaults })}
          >
            Reset to defaults
          </button>
          <button type="button" className="styled-button cancel" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="styled-button" onClick={apply} disabled={!valid}>
            Apply
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default MapActionEditModal
